import { useEffect, useMemo, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { Info, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';

const fmtWeek = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}`;
  } catch { return iso; }
};

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 }));

/**
 * Аналитический график средневзвешенных цен.
 * Использует фильтры родителя: productName, bidEndFrom, bidEndTo, supplierCompany.
 * Никаких собственных фильтров не рендерит — единая точка управления у пользователя.
 */
export default function WeightedPricesChart({ productName, bidEndFrom, bidEndTo, supplierCompany, currencyMode = 'native', currency = '', direction = '' }) {
  const [data, setData] = useState({ weeks: [], tender_count: 0, mode: 'winner' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (bidEndFrom) params.date_from = bidEndFrom;
        if (bidEndTo) params.date_to = bidEndTo;
        if (productName) params.product = productName;
        if (supplierCompany) params.supplier_company = supplierCompany;
        if (currencyMode && currencyMode !== 'native' && currency) {
          params.currency_mode = currencyMode;
          params.currency = currency;
        }
        if (direction) params.direction = direction;
        const { data } = await api.get('/admin/archive/analytics/weighted-prices', { params });
        if (!cancelled) setData(data || { weeks: [], tender_count: 0, mode: 'winner' });
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Не удалось загрузить аналитику');
          setData({ weeks: [], tender_count: 0, mode: 'winner' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bidEndFrom, bidEndTo, productName, supplierCompany, currencyMode, currency, direction]);

  const isSupplier = data.mode === 'supplier';
  const displayCurrency = data.currency || ''; // эффективная валюта (filter/convert) или ''
  // Подписи легенды/таблицы зависят от режима: «победитель» vs «выбранный поставщик»
  const labelFirst = isSupplier ? 'Поставщик: первая ставка' : 'Победитель: первая ставка';
  const labelFinal = isSupplier ? 'Поставщик: финальная цена' : 'Цена победителя';
  const labelFirstShort = isSupplier ? 'Первая (взв.)' : 'Первая (взв.)';
  const labelFinalShort = isSupplier ? 'Финал (взв.)' : 'Победитель (взв.)';

  const chartData = useMemo(
    () => (data.weeks || []).map(w => ({
      week: fmtWeek(w.week_start),
      week_start: w.week_start,
      ceiling: w.weighted_ceiling,
      winning: w.weighted_winning,
      first_winner: w.weighted_first_winner,
      volume: w.total_volume,
      tenders: w.tender_count,
      lots: w.tenders || [],
    })),
    [data]
  );

  const navigate = useNavigate();
  // Какие недели раскрыты (Set<week_start>)
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set());
  const toggleWeek = (wk) => {
    setExpandedWeeks(prev => {
      const n = new Set(prev);
      if (n.has(wk)) n.delete(wk); else n.add(wk);
      return n;
    });
  };
  const openTender = (tenderId) => {
    if (!tenderId) return;
    navigate(`/dashboard?tender=${encodeURIComponent(tenderId)}`);
  };

  // Динамические оси: добавим воздух, чтобы линии и метки не липли к краям.
  const yPriceDomain = useMemo(() => {
    if (!chartData.length) return [0, 'auto'];
    const vals = chartData
      .flatMap(d => [d.ceiling, d.winning, d.first_winner])
      .filter(v => v != null);
    if (!vals.length) return [0, 'auto'];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.15 || max * 0.15 || 100;
    return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
  }, [chartData]);

  const yVolumeDomain = useMemo(() => {
    if (!chartData.length) return [0, 'auto'];
    const max = Math.max(...chartData.map(d => d.volume || 0));
    return [0, Math.ceil(max * 1.25) || 10];
  }, [chartData]);

  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-sm p-4" data-testid="weighted-prices-chart">
      {loading ? (
        <div className="py-10 text-center text-xs text-slate-400" data-testid="analytics-loading">
          Загрузка аналитики…
        </div>
      ) : error ? (
        <div className="py-6 px-4 bg-red-50 border border-red-200 text-xs text-red-700 rounded-sm flex items-start gap-2"
          data-testid="analytics-error">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : chartData.length === 0 ? (
        <div className="py-6 px-4 bg-amber-50 border border-amber-200 text-xs text-amber-800 rounded-sm flex items-start gap-2"
          data-testid="analytics-empty">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Нет завершённых редукционов под выбранные фильтры. Расширьте диапазон дат
            «Конец торгов» выше или сбросьте товар.
          </span>
        </div>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between text-[11px] text-slate-500" data-testid="analytics-meta">
            <span>
              {isSupplier && (
                <span className="inline-block mr-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-sm font-semibold">
                  По поставщику: {supplierCompany}
                </span>
              )}
              {displayCurrency && (
                <span className="inline-block mr-2 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm font-semibold"
                  data-testid="analytics-currency-badge">
                  {data.currency_mode === 'convert'
                    ? `Приведено к ${displayCurrency} (кросс-курс ЦБ)`
                    : `Только ${displayCurrency}`}
                </span>
              )}
              {data.fx_missing > 0 && (
                <span className="inline-block mr-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm"
                  data-testid="analytics-fx-missing"
                  title="Тендеры пропущены: курс ЦБ на дату окончания торгов недоступен">
                  Без курса: {data.fx_missing}
                </span>
              )}
              {isSupplier ? 'Тендеров с участием поставщика' : 'Завершённых тендеров'}: <span className="font-semibold text-slate-700">{data.tender_count}</span>
              {' · '}Недель с данными: <span className="font-semibold text-slate-700">{chartData.length}</span>
              {' · '}Средн. снижение от потолка: <span className="font-semibold text-red-700">{
                (() => {
                  const num = chartData.reduce((s, r) => s + (r.ceiling - r.winning) * r.volume, 0);
                  const den = chartData.reduce((s, r) => s + r.ceiling * r.volume, 0);
                  return den > 0 ? `${((num / den) * 100).toFixed(2)}%` : '—';
                })()
              }</span>
              {' · '}Средн. снижение в торгах: <span className="font-semibold text-blue-700">{
                (() => {
                  const num = chartData.reduce((s, r) => s + (r.first_winner - r.winning) * r.volume, 0);
                  const den = chartData.reduce((s, r) => s + r.first_winner * r.volume, 0);
                  return den > 0 ? `${((num / den) * 100).toFixed(2)}%` : '—';
                })()
              }</span>
            </span>
            <span className="text-slate-400 italic">
              Каждая неделя — это понедельник; точка соответствует средневзвешенной по объёму цене.
            </span>
          </div>
          <div style={{ width: '100%', height: 380 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 24, right: 28, left: 8, bottom: 8 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#475569' }} padding={{ left: 28, right: 28 }} />
                <YAxis yAxisId="price" domain={yPriceDomain} tick={{ fontSize: 11, fill: '#475569' }}
                  label={{ value: displayCurrency ? `Цена, ${displayCurrency}` : 'Цена', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#475569' } }} />
                <YAxis yAxisId="volume" orientation="right" domain={yVolumeDomain} tick={{ fontSize: 11, fill: '#475569' }}
                  label={{ value: 'Объём, т', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#475569' } }} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 2 }}
                  formatter={(value, name) => [fmtNum(value), name]}
                  labelFormatter={(label, payload) => {
                    const p = payload?.[0]?.payload;
                    return p?.week_start ? `Неделя с ${new Date(p.week_start).toLocaleDateString('ru-RU')}` : label;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="volume" dataKey="volume" name="Объём (тонны)" fill="#cbd5e1" barSize={40}>
                  <LabelList dataKey="volume" position="top" formatter={fmtNum}
                    style={{ fontSize: 10, fill: '#64748b' }} />
                </Bar>
                <Line yAxisId="price" type="monotone" dataKey="ceiling" name="Потолок цены"
                  stroke="#dc2626" strokeDasharray="6 3" strokeWidth={2} dot={{ r: 4 }}>
                  <LabelList dataKey="ceiling" position="top" formatter={fmtNum}
                    style={{ fontSize: 10, fill: '#b91c1c', fontWeight: 600 }} />
                </Line>
                <Line yAxisId="price" type="monotone" dataKey="first_winner" name={labelFirst}
                  stroke="#2563eb" strokeDasharray="2 3" strokeWidth={2} dot={{ r: 4 }}>
                  <LabelList dataKey="first_winner" position="top" formatter={fmtNum}
                    style={{ fontSize: 10, fill: '#1d4ed8', fontWeight: 600 }} />
                </Line>
                <Line yAxisId="price" type="monotone" dataKey="winning" name={labelFinal}
                  stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }}>
                  <LabelList dataKey="winning" position="bottom" formatter={fmtNum}
                    style={{ fontSize: 10, fill: '#15803d', fontWeight: 600 }} />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Краткая сводка под графиком: неделя + раскрытие лотов внутри */}
          <div className="mt-4 border-t border-slate-200 pt-3 overflow-x-auto">
            <table className="w-full text-[11px] tabular-nums" data-testid="analytics-table">
              <thead>
                <tr className="text-slate-500 border-b border-slate-100">
                  <th className="w-6"></th>
                  <th className="text-left font-medium py-1.5 pr-3">Неделя (с)</th>
                  <th className="text-right font-medium py-1.5 px-3">Тендеров</th>
                  <th className="text-right font-medium py-1.5 px-3">Объём, т</th>
                  <th className="text-right font-medium py-1.5 px-3 text-red-700">Потолок (взв.)</th>
                  <th className="text-right font-medium py-1.5 px-3 text-blue-700">{labelFirstShort}</th>
                  <th className="text-right font-medium py-1.5 px-3 text-green-700">{labelFinalShort}</th>
                  <th className="text-right font-medium py-1.5 px-3 text-red-700"
                    title="Снижение от потолка цены к финальной цене победителя">
                    Δ от потолка
                  </th>
                  <th className="text-right font-medium py-1.5 pl-3 text-blue-700"
                    title="Снижение от первой ставки победителя к его финальной цене — эффект торгов">
                    Δ в торгах
                  </th>
                </tr>
              </thead>
              <tbody>
                {chartData.map(row => {
                  const ceilSavingAbs = (row.ceiling - row.winning) * row.volume;
                  const ceilPct = row.ceiling > 0 ? ((row.ceiling - row.winning) / row.ceiling) * 100 : 0;
                  const auctionSavingAbs = (row.first_winner - row.winning) * row.volume;
                  const auctionPct = row.first_winner > 0
                    ? ((row.first_winner - row.winning) / row.first_winner) * 100
                    : 0;
                  const isOpen = expandedWeeks.has(row.week_start);
                  const lots = row.lots || [];
                  return (
                    <Fragment key={row.week_start}>
                      <tr
                        className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer select-none"
                        onClick={() => toggleWeek(row.week_start)}
                        data-testid={`analytics-week-${row.week_start}`}
                        title="Кликните, чтобы развернуть лоты этой недели">
                        <td className="text-center text-slate-400">
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5 inline" /> : <ChevronRight className="w-3.5 h-3.5 inline" />}
                        </td>
                        <td className="py-1.5 pr-3 text-slate-700 font-medium">
                          {new Date(row.week_start).toLocaleDateString('ru-RU')}
                        </td>
                        <td className="py-1.5 px-3 text-right text-slate-600">{row.tenders}</td>
                        <td className="py-1.5 px-3 text-right text-slate-600">{fmtNum(row.volume)}</td>
                        <td className="py-1.5 px-3 text-right text-red-700">{fmtNum(row.ceiling)}</td>
                        <td className="py-1.5 px-3 text-right text-blue-700">{fmtNum(row.first_winner)}</td>
                        <td className="py-1.5 px-3 text-right text-green-700">{fmtNum(row.winning)}</td>
                        <td className="py-1.5 px-3 text-right text-red-700">
                          {ceilPct.toFixed(2)}% <span className="text-slate-400">({fmtNum(ceilSavingAbs)})</span>
                        </td>
                        <td className="py-1.5 pl-3 text-right text-blue-700">
                          {auctionPct.toFixed(2)}% <span className="text-slate-400">({fmtNum(auctionSavingAbs)})</span>
                        </td>
                      </tr>

                      {isOpen && lots.length > 0 && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={9} className="p-0">
                            <table className="w-full text-[11px] tabular-nums">
                              <thead>
                                <tr className="text-slate-400 border-b border-slate-200">
                                  <th className="w-6"></th>
                                  <th className="text-left font-medium py-1 pr-3 pl-2 italic">
                                    Лот · конец торгов
                                  </th>
                                  <th className="text-left font-medium py-1 px-3">Товар</th>
                                  <th className="text-right font-medium py-1 px-3">Объём, т</th>
                                  <th className="text-right font-medium py-1 px-3 text-red-700">Потолок</th>
                                  <th className="text-right font-medium py-1 px-3 text-blue-700">Первая</th>
                                  <th className="text-right font-medium py-1 px-3 text-green-700">Финал</th>
                                  <th className="text-right font-medium py-1 px-3 text-red-700">Δ потолок</th>
                                  <th className="text-right font-medium py-1 pl-3 text-blue-700">Δ торги</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lots.map(lot => {
                                  const lotCeilPct = lot.ceiling > 0
                                    ? ((lot.ceiling - lot.winning) / lot.ceiling) * 100 : 0;
                                  const lotAuctionPct = lot.first_winner > 0
                                    ? ((lot.first_winner - lot.winning) / lot.first_winner) * 100 : 0;
                                  const endStr = lot.bid_end
                                    ? new Date(lot.bid_end).toLocaleDateString('ru-RU')
                                    : '—';
                                  return (
                                    <tr key={lot.tender_id}
                                      className="hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                                      onDoubleClick={() => openTender(lot.tender_id)}
                                      title="Двойной клик — перейти в карточку тендера"
                                      data-testid={`analytics-lot-${lot.tender_id}`}>
                                      <td className="text-center text-slate-300">
                                        <ExternalLink className="w-3 h-3 inline" />
                                      </td>
                                      <td className="py-1 pr-3 pl-2 text-slate-700">
                                        <div className="truncate max-w-[260px]" title={lot.lot_name}>
                                          {lot.lot_name || '(без названия)'}
                                        </div>
                                        <div className="text-[10px] text-slate-400">{endStr}</div>
                                      </td>
                                      <td className="py-1 px-3 text-slate-600 truncate max-w-[180px]" title={lot.product_name}>
                                        {lot.product_name || '—'}
                                      </td>
                                      <td className="py-1 px-3 text-right text-slate-600">{fmtNum(lot.volume)}</td>
                                      <td className="py-1 px-3 text-right text-red-700">{fmtNum(lot.ceiling)}</td>
                                      <td className="py-1 px-3 text-right text-blue-700">{fmtNum(lot.first_winner)}</td>
                                      <td className="py-1 px-3 text-right text-green-700">{fmtNum(lot.winning)}</td>
                                      <td className="py-1 px-3 text-right text-red-700">{lotCeilPct.toFixed(2)}%</td>
                                      <td className="py-1 pl-3 text-right text-blue-700">{lotAuctionPct.toFixed(2)}%</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-2 text-[10px] text-slate-400 italic">
              Кликните по строке недели — развернётся список лотов внутри неё. Двойной клик по лоту — открыть тендер в Панели управления.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
